import React, { useCallback, useEffect, useRef, type ReactNode, useState } from 'react';

import { Box } from '@atlaskit/primitives';
import Popup, { type TriggerProps } from '@atlaskit/popup';

const FADE_IN_DELAY = 500;
const FADE_OUT_DELAY = 300;

export type HoverPopupProps = {
	children: ReactNode;
	renderContent: ReactNode | ((update: () => void) => ReactNode);
	canOpen?: boolean;
	closeOnChildClick?: boolean;
	allowEventPropagation?: boolean;
	zIndex?: number;
	noFadeDelay?: boolean;
	fadeInDelay?: number;
	onVisibilityChange?: (isVisible: boolean) => void;
};

// This is blatantly copied and modified from a smart card's HoverCard in platform/packages/linking-platform/smart-card/src/view/HoverCard/components/HoverCardComponent.tsx
export const HoverPopup = ({
	children,
	renderContent: popupContent,
	canOpen = true,
	closeOnChildClick = false,
	allowEventPropagation = false,
	zIndex = 510,
	noFadeDelay = false,
	fadeInDelay: customFadeInDelay,
	onVisibilityChange,
}: HoverPopupProps): React.JSX.Element => {
	const fadeInDelay = customFadeInDelay ?? FADE_IN_DELAY;
	const [isOpen, setIsOpen] = useState(false);
	const fadeOutTimeoutId = useRef<ReturnType<typeof setTimeout>>();
	const fadeInTimeoutId = useRef<ReturnType<typeof setTimeout>>();
	const mousePos = useRef<{ x: number; y: number }>();
	const popupOffset = useRef<[number, number]>();
	const parentSpan = useRef<HTMLSpanElement>(null);

	const handleSetIsOpen = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			onVisibilityChange?.(open);
		},
		[onVisibilityChange],
	);

	const setMousePosition = useCallback(
		(event: React.MouseEvent) => {
			if (isOpen && canOpen) {
				return;
			}
			mousePos.current = {
				x: event.clientX,
				y: event.clientY,
			};

			// If these are undefined then popupOffset is undefined and we fallback to default bottom-start placement
			if ((!isOpen || !canOpen) && parentSpan.current && mousePos.current) {
				const { bottom, left } = parentSpan.current.getBoundingClientRect();
				const CARD_GAP_PX = 8;
				popupOffset.current = [
					mousePos.current.x - left + CARD_GAP_PX,
					mousePos.current.y - bottom + CARD_GAP_PX,
				];
			}
		},
		[canOpen, isOpen],
	);

	const hideCard = useCallback(() => {
		handleSetIsOpen(false);
	}, [handleSetIsOpen]);

	const initHideCard = useCallback(() => {
		if (fadeInTimeoutId.current) {
			clearTimeout(fadeInTimeoutId.current);
			fadeInTimeoutId.current = undefined;
		}

		if (noFadeDelay) {
			hideCard();
		} else {
			fadeOutTimeoutId.current = setTimeout(() => hideCard(), FADE_OUT_DELAY);
		}
	}, [hideCard, noFadeDelay]);

	// clearing out the timeouts in order to avoid memory leaks
	// in case the component unmounts before they execute
	useEffect(() => {
		return () => {
			if (fadeOutTimeoutId.current) {
				clearTimeout(fadeOutTimeoutId.current);
			}

			if (fadeInTimeoutId.current) {
				clearTimeout(fadeInTimeoutId.current);
				fadeInTimeoutId.current = undefined;
			}
		};
	}, []);

	const initShowCard = useCallback(
		(event: React.MouseEvent | React.FocusEvent) => {
			// clearing out fadeOutTimeoutId in case it's already counting down to hide the card
			if (fadeOutTimeoutId.current) {
				clearTimeout(fadeOutTimeoutId.current);
			}

			// Set mouse position in the case it's not already set by onMouseMove, as in the case of scrolling
			if ('clientX' in event && 'clientY' in event) {
				const mouseEvent = event as unknown as React.MouseEvent; // eslint-disable-line @typescript-eslint/consistent-type-assertions
				setMousePosition(mouseEvent);
			}

			if (!isOpen && !fadeInTimeoutId.current) {
				// setting a timeout to show a Hover Card after delay runs out
				if (noFadeDelay) {
					handleSetIsOpen(true);
				} else {
					fadeInTimeoutId.current = setTimeout(() => {
						handleSetIsOpen(true);
					}, fadeInDelay);
				}
			}
		},
		[isOpen, setMousePosition, noFadeDelay, fadeInDelay, handleSetIsOpen],
	);

	// Stop hover preview content to propagate event to parent.
	const handleChildClick = useCallback(
		(e: React.MouseEvent, closeOnClick: boolean) => {
			if (!allowEventPropagation) {
				e.stopPropagation();
			}

			if (closeOnClick) {
				hideCard();
			}
		},
		[allowEventPropagation, hideCard],
	);

	const onContextMenuClick = useCallback(
		(e: React.MouseEvent) => handleChildClick(e, true),
		[handleChildClick],
	);

	const onChildClick = useCallback(
		(e: React.MouseEvent) => handleChildClick(e, closeOnChildClick),
		[closeOnChildClick, handleChildClick],
	);

	const content = useCallback(
		({ update }: { update: () => void }) => {
			const contentElement =
				typeof popupContent === 'function' ? popupContent(update) : popupContent;

			return (
				<Box
					onMouseEnter={initShowCard}
					onFocus={initShowCard}
					onMouseLeave={initHideCard}
					onBlur={initHideCard}
				>
					{contentElement}
				</Box>
			);
		},
		[initShowCard, initHideCard, popupContent],
	);

	const trigger = useCallback(
		({
			'aria-haspopup': ariaHasPopup,
			'aria-expanded': ariaExpanded,
			...triggerProps
		}: TriggerProps) => (
			<Box as="span" ref={parentSpan}>
				{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions */}
				<Box
					as="span"
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...triggerProps}
					onMouseEnter={initShowCard}
					onFocus={initShowCard}
					onMouseLeave={initHideCard}
					onBlur={initHideCard}
					onMouseMove={setMousePosition}
					onClick={onChildClick}
					onContextMenu={onContextMenuClick}
					testId="issue-field-atlas-project-common.ui.hover-popup.hover-popup-trigger-wrapper"
				>
					{children}
				</Box>
			</Box>
		),
		[children, initHideCard, initShowCard, onChildClick, onContextMenuClick, setMousePosition],
	);

	return (
		<Popup
			testId="issue-field-atlas-project-common.ui.hover-popup.hover-popup"
			isOpen={isOpen && canOpen}
			onClose={hideCard}
			placement="bottom-start"
			offset={popupOffset.current}
			autoFocus={false}
			content={content}
			trigger={trigger}
			zIndex={zIndex}
		/>
	);
};
